// User-facing error text + toasts.
//
// Errors reach the UI in many shapes — axios errors, parsed fetch bodies
// ({detail}), thrown Errors, FastAPI validation lists, HTML gateway pages —
// and were being rendered raw ("Failed to fetch", "[object Object]",
// "Unexpected token '<'…", Python tracebacks). `friendlyError` turns any of
// them into one short sentence; `notifyError` shows it as a toast.
// `installGlobalErrorToasts` catches request failures nothing handled.
import { toast } from 'sonner';

export const DEFAULT_ERROR = 'Something went wrong. Please try again.';

export const MESSAGES = {
  network: "Can't reach the server. Check your internet connection and try again.",
  timeout: 'The server took too long to respond. Please try again.',
  unavailable: 'The server is temporarily unavailable. Please try again in a minute.',
  session: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to do that.",
  tooLarge: 'That file is too large to upload.',
  busy: 'Too many requests right now. Please wait a moment and try again.',
};

const STATUS_MESSAGES = {
  401: MESSAGES.session,
  403: MESSAGES.forbidden,
  413: MESSAGES.tooLarge,
  429: MESSAGES.busy,
  502: MESSAGES.unavailable,
  503: MESSAGES.unavailable,
  504: MESSAGES.unavailable,
};

// Longer than this is a dump (stack trace, HTML, serialized object), not a message.
const MAX_MESSAGE_LENGTH = 300;

// Text that is never fit to show a user: runtime errors, Python exceptions,
// provider/infra internals, serialized objects, generic HTTP phrases.
const TECHNICAL_PATTERNS = [
  /traceback|\bfile ".*", line \d+/i,
  /\b\w*(exception|error):\s/i,
  /\bundefined\b|\bnull is not\b|is not a function|is not defined|cannot read propert/i,
  /unexpected token|is not valid json|json\.parse/i,
  /failed to fetch|networkerror|network error|\bload failed|network request failed/i,
  /status code \d{3}|error code: \d+|\berrno\b|\becon\w+\b|\berr_[a-z_]+\b/i,
  /\((?:http\s*)?\d{3}\)/i, // "Request failed (500)", "Upload failed (HTTP 502)"
  /<!doctype|<html|\[object object\]|\{['"]|\bobject at 0x/i,
  /ffmpeg|whisper|openai|anthropic|gemini|api[ _-]?key|pymongo|\bmongo\b|motor|uvicorn|starlette|pydantic|httpx|\bssl\b/i,
  /^(internal server error|not found|method not allowed|bad request|unprocessable entity|request failed)\.?$/i,
];

// Auth-layer details that really mean "sign in again" whatever the status.
const SESSION_DETAIL = /^(not authenticated|could not validate credentials|invalid token|token (has )?expired|signature has expired)\.?$/i;

const NON_FIELD_LOC = new Set(['body', 'query', 'path', 'header', 'cookie']);

// Fallbacks like "Failed" tell the user nothing — use the default instead.
const UNINFORMATIVE_FALLBACK = /^(failed|error|oops|something went wrong)[.!]*$/i;

const isReadable = (text) => {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_MESSAGE_LENGTH && !TECHNICAL_PATTERNS.some((re) => re.test(trimmed));
};

const lowerFirst = (s) => s.charAt(0).toLowerCase() + s.slice(1);

/** FastAPI 422 `detail: [{loc, msg}]` → "Please check email: value is not a valid email address". */
function fromValidationList(list) {
  const items = list.filter((x) => x && typeof x === 'object' && typeof x.msg === 'string' && x.msg.trim());
  if (items.length === 0) return null;
  const [first] = items;
  const field = (Array.isArray(first.loc) ? first.loc : [])
    .filter((part) => typeof part === 'string' && !NON_FIELD_LOC.has(part))
    .pop();
  const more = items.length > 1 ? ` (and ${items.length - 1} more)` : '';
  const text = field ? `Please check ${field}: ${lowerFirst(first.msg.trim())}${more}` : `${first.msg.trim()}${more}`;
  return isReadable(text) ? text : null;
}

function fromDetail(detail) {
  if (typeof detail === 'string') {
    if (SESSION_DETAIL.test(detail.trim())) return MESSAGES.session;
    return isReadable(detail) ? detail.trim() : null;
  }
  if (Array.isArray(detail)) return fromValidationList(detail);
  if (detail && typeof detail === 'object') {
    const candidate = [detail.message, detail.msg, detail.error].find((v) => typeof v === 'string');
    return candidate ? fromDetail(candidate) : null;
  }
  return null;
}

const isCancellation = (err) =>
  err?.code === 'ERR_CANCELED' || err?.name === 'AbortError' || err?.name === 'CanceledError';

const isNetworkFailure = (err) =>
  err?.code === 'ERR_NETWORK'
  || (err?.isAxiosError && !err.response && /network error/i.test(err?.message || ''))
  || (err?.name === 'TypeError' && /failed to fetch|networkerror|\bload failed|network request failed/i.test(err?.message || ''));

const isTimeout = (err) =>
  err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT'
  || (err?.isAxiosError && !err.response && /timeout/i.test(err?.message || ''));

const isHtmlInsteadOfJson = (err) =>
  err?.name === 'SyntaxError' && /unexpected token|is not valid json|json/i.test(err?.message || '');

/** The server's own message, wherever this error shape keeps it. */
function serverMessage(err) {
  const data = err.response ? err.response.data : (err.body ?? err.data ?? err);
  if (!data || typeof data !== 'object') return null;
  return fromDetail(data.detail) ?? fromDetail(data.error) ?? (data === err ? null : fromDetail(data.message));
}

/**
 * One short, user-safe sentence for any error-ish value.
 * @param {unknown} err  axios error, fetch body, Error, string, detail value…
 * @param {string} [fallback]  context-specific text used when nothing better is known
 */
export function friendlyError(err, fallback = DEFAULT_ERROR) {
  const safeFallback = isReadable(fallback) && !UNINFORMATIVE_FALLBACK.test(fallback.trim()) ? fallback : DEFAULT_ERROR;
  if (typeof err === 'string') return fromDetail(err) ?? safeFallback;
  if (!err || typeof err !== 'object') return safeFallback;

  if (isNetworkFailure(err)) return MESSAGES.network;
  if (isTimeout(err)) return MESSAGES.timeout;
  if (isHtmlInsteadOfJson(err)) return MESSAGES.unavailable;

  const fromServer = serverMessage(err);
  if (fromServer) return fromServer;

  const status = err.response?.status ?? (typeof err.status === 'number' ? err.status : undefined);
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  if (!isCancellation(err) && !err.isAxiosError && typeof err.message === 'string' && isReadable(err.message)) {
    return err.message.trim();
  }
  return safeFallback;
}

/**
 * Show `err` as a friendly error toast (identical messages collapse into one).
 * @returns {string} the text shown
 */
export function notifyError(err, fallback, options = {}) {
  const message = friendlyError(err, fallback);
  toast.error(message, { id: `error:${message}`, ...options });
  return message;
}

/** A failed request (as opposed to a bug in some library) — worth telling the user about. */
export function isApiFailure(reason) {
  if (!reason || isCancellation(reason)) return false;
  return !!reason.isAxiosError || isNetworkFailure(reason) || isHtmlInsteadOfJson(reason);
}

/** Toast request failures that no caller handled. Install once at startup. */
export function installGlobalErrorToasts() {
  if (window.__deskeyErrorToasts) return;
  window.__deskeyErrorToasts = true;
  window.addEventListener('unhandledrejection', (event) => {
    if (!isApiFailure(event.reason)) return;
    console.warn('[deskey] unhandled request failure', event.reason);
    notifyError(event.reason);
  });
}
